import { useState } from 'react'
import { Button, Popover, Textarea, UnstyledButton } from '@mantine/core'
import { IconPencil } from '@tabler/icons-react'
import { useAutofocusOnOpen, useUpdateItemValue, type CellProps } from '../hooks'

// The cell shows a truncated preview; clicking opens a popover with a full
// multi-line editor so long values stay readable in the narrow column.
export const TextCell = ({ item, column }: CellProps) => {
  const { update } = useUpdateItemValue(item)
  const raw = item.values[column.id]
  const stored = typeof raw === 'string' ? raw : ''
  const [opened, setOpened] = useState(false)
  const [draft, setDraft] = useState(stored)
  const draftRef = useAutofocusOnOpen<HTMLTextAreaElement>(opened)

  const toggle = () => {
    if (!opened) setDraft(stored)
    setOpened((prev) => !prev)
  }
  const close = () => {
    const value = draft.trim()
    if (value !== stored) void update(column.id, value || null)
    setOpened(false)
  }

  return (
    <Popover
      opened={opened}
      // Fires on outside click / Escape; commit the draft on dismissal.
      onChange={(next) => !next && close()}
      width={320}
      position="bottom"
      shadow="md"
      withinPortal
      trapFocus={false}
    >
      <Popover.Target>
        <UnstyledButton
          aria-label={`${column.label}: ${stored || 'empty'}`}
          onClick={toggle}
          className="flex w-full cursor-pointer items-center truncate rounded py-1 pr-3 text-left text-[13.5px] transition hover:bg-gray-50"
        >
          {/* Empty cell shows a faint pencil so it reads as editable. */}
          {stored || <IconPencil size={14} className="text-gray-300" />}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className="p-2">
        <Textarea
          ref={draftRef}
          autosize
          minRows={3}
          maxRows={12}
          placeholder={`Add ${column.label.toLowerCase()}…`}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
        />
        <div className="mt-2 flex justify-end">
          <Button size="xs" onClick={close}>
            Save
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}
